import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type DataTag,
  type UndefinedInitialDataOptions,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CommissionCalculation } from '@cda/shared/types/domain';
import { useAuthStore } from '@shared/stores/auth.store';
import type { ImportCommitResponse, ImportPreviewResponse } from '../shared/import/types';

/**
 * @fileoverview API client utilities and typed contracts for server communication
 *
 * @changeHistory
 * - 2026-02-08: Align dues status fields with server response payload (Neer Patel)
 * - 2026-07-07: Added shared query options for route label and detail reuse (Codex)
 * - 2026-07-23: Added Team leadership list and update contracts
 * - 2026-07-28: Added website-visibility contracts and mutation hook (GitHub Copilot)
 * - 2026-07-31: Added unified Team member update contract and mutation (Codex)
 * - 2026-07-31: Added Team table sorting and soft-deactivation contracts (Codex)
 * - 2026-08-13: Unified Team roles, account, public profile, visibility, and deletion contracts (Codex)
 * - 2026-09-30: Added broker/admin agent property image uploads (Copilot)
 */

export const API_BASE_URL = (import.meta.env?.VITE_API_URL as string | undefined) || '/api/v1';

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const { accessToken } = useAuthStore.getState();

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...options.headers,
    },
    credentials: 'include', // Send cookies for JWT authentication
  };

  try {
    const response = await fetch(url, config);

    // Handle non-JSON responses (like 204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    const data = (await response.json()) as { message?: string };

    if (!response.ok) {
      // Handle session timeout/unauthorized access
      if (response.status === 401) {
        useAuthStore.getState().logout();
      }
      throw new ApiError(data.message || 'An error occurred', response.status, data);
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or parsing errors
    throw new ApiError(error instanceof Error ? error.message : 'Network error', 0);
  }
}

/**
 * API client methods
 */
export const apiClient = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    fetchApi<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    fetchApi<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    fetchApi<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    fetchApi<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestInit) =>
    fetchApi<T>(endpoint, { ...options, method: 'DELETE' }),
};

/**
 * Type definitions for API responses
 */

// 3. API Response Wrappers
export interface PlanSingleApiResponse {
  status: 'success';
  message: string;
  data: PlanApiPayload;
}

export interface PlanListApiResponse {
  status: 'success';
  message: string;
  data: PlanApiPayload[];
}

export interface CreatePlanResponse {
  status: 'success';
  message: string;
  data: PlanApiPayload;
}

// Auth
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  status: string;
  message: string;
  data: {
    user: {
      id: string;
      email: string;
      fullName: string;
      firstName: string;
      lastName: string;
      role: 'agent' | 'broker' | 'admin' | 'transaction_coordinator';
      phone?: string;
    };
    accessToken: string;
    refreshToken: string;
  };
}

export interface AuthAgentCompensation {
  currentPlan: {
    id: number;
    planName: string;
    status: 'ACTIVE' | 'INACTIVE';
  } | null;
  cap: {
    earnedThisYear: number;
    capAmount: number;
    remaining: number;
    percentUsed: number;
    capEnforced: boolean;
    renewalDate: string | null;
  };
}

export interface MeResponse {
  status: string;
  message: string;
  data: {
    user: {
      id: string;
      email: string;
      fullName: string;
      firstName: string;
      lastName: string;
      role: 'agent' | 'broker' | 'admin' | 'transaction_coordinator';
      phone?: string;
      agentId?: string;
    };
    agentCompensation: AuthAgentCompensation | null;
  };
}

// Commission Disbursement
export interface CommissionDisbursementSubmission {
  agentId?: string;
  agentName: string;
  agentEmail: string;
  agentPhone?: string;
  propertyAddress: string;
  propertyCity?: string;
  propertyState: string;
  propertyZip?: string;
  // Legacy aliases accepted by backend for backward compatibility.
  gfFileNumber?: string;
  mlsNumber?: string;
  contractDate: Date;
  expires?: Date;
  representation: 'buyer' | 'seller' | 'tenant' | 'landlord';
  leaseCommissionBasis?: 'monthly' | 'annual';
  propertyClass?: 'residential' | 'commercial';
  sellerAgent?: string;
  buyerAgent?: string;
  // Legacy aliases accepted by backend for backward compatibility.
  titleCompanyName?: string;
  titleCompanyContact?: string;
  titleCompanyEmail?: string;
  titleCompanyPhone?: string;
  escrowNumber?: string;
  escrowCompany?: string;
  escrowAddress?: string;
  escrowPhone?: string;
  escrowFax?: string;
  escrowAgent?: string;
  escrowAgentEmail?: string;
  purchasePrice: number;
  commissionRate?: number;
  commissionFlatFee?: number;
  totalCommissionDue: number;
  brokerTransactionFee: number;
  eoInsuranceFee: number;
  annualDues: number;
  duesPaymentDetails?: Array<{
    month: string;
    amount: number;
    type?: string;
  }>;
  duesDeductionOption?: 'current_balance' | 'anniversary' | 'none';
  duesDeductionAmount?: number;
  duesDeductionMonths?: Array<{
    month: string;
    amount: number;
  }>;
  otherDeductions: number;
  otherDeductionsDescription?: string;
  totalOfficeFees: number;
  agentCommissionPercentage: number;
  netAgentCommission: number;
  bonusAmount: number;
  bonusDescription?: string;
  referringBrokerName?: string;
  referringBrokerCompany?: string;
  referringBrokerEmail?: string;
  referringBrokerFee: number;
  agentCreditAppliedTo?: 'buyer' | 'seller' | 'buyer_closing_cost' | 'seller_closing_cost';
  agentCreditAmount: number;
  agentCreditAcknowledgement: boolean;
  depositReleasedToBrokerage: number;
  internalNotes?: string;
  agentNotes?: string;
}

export interface CommissionDisbursement extends CommissionDisbursementSubmission {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'canceled';
  version: number;
  submissionDate: string;
  submissionSource: 'web_app' | 'mobile_app' | 'api';
  agentId: string;
  approvedBy?: string;
  approvedByEmail?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  rejectionComment?: string;
  canceledBy?: string;
  canceledAt?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
  lastModified?: string;
}

export interface SubmitCDAResponse {
  data: CommissionDisbursement;
  message: string;
}

export interface CdaListResponse {
  success: boolean;
  data: CommissionDisbursement[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
    timestamp: string;
  };
}

// Type aliases for consistency with hook naming
export type CreateCDARequest = CommissionDisbursementSubmission;
export type CreateCDAResponse = SubmitCDAResponse;

export interface SubmissionFeedbackMessage {
  field: string;
  message: string;
  code: string;
}

export type AutoPopulatedField =
  | 'brokerTransactionFee'
  | 'eoInsuranceFee'
  | 'monthlyDues'
  | 'agentCommissionPercentage';

export interface AppliedPlanSummary {
  id: number;
  planName: string;
  isPostCap?: boolean;
  postCapTransactionFeeRequired?: boolean;
}

export interface ValidateCDAResponse {
  status: 'success' | 'error';
  success: boolean;
  message: string;
  data: {
    isValid: boolean;
    calculation?: CommissionCalculation;
    warnings: SubmissionFeedbackMessage[];
    autoPopulatedFields: AutoPopulatedField[];
    appliedPlan: AppliedPlanSummary | null;
    isPostCap?: boolean;
  };
}

/**
 * Auth API calls
 */
export const authApi = {
  login: (data: LoginRequest) => apiClient.post<LoginResponse>('/auth/login', data),

  logout: () => apiClient.post('/auth/logout'),

  me: () => apiClient.get<MeResponse>('/auth/me'),
};

/**
 * CDA API calls
 */
export const cdaApi = {
  submit: (data: CommissionDisbursementSubmission) =>
    apiClient.post<SubmitCDAResponse>('/cdas', data),

  validate: (data: CommissionDisbursementSubmission) =>
    apiClient.post<ValidateCDAResponse>('/cdas/validate', data),

  getAll: (params?: {
    status?: string;
    search?: string;
    sort?: string;
    agentId?: string;
    limit?: string | number;
    offset?: string | number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const queryParams = new URLSearchParams(params as Record<string, string>).toString();
    return apiClient.get<CdaListResponse>(`/cdas${queryParams ? `?${queryParams}` : ''}`);
  },

  getById: (id: string) => apiClient.get<{ data: CommissionDisbursement }>(`/cdas/${id}`),

  approve: (id: string, approvalNotes?: string) =>
    apiClient.patch<{ data: CommissionDisbursement }>(`/cdas/${id}/approve`, {
      approvalNotes,
    }),

  reject: (id: string, reason: string, comment?: string) =>
    apiClient.patch<{ data: CommissionDisbursement }>(`/cdas/${id}/reject`, {
      reason,
      comment,
    }),

  cancel: (id: string, reason: string, notes?: string) =>
    apiClient.patch<{ data: CommissionDisbursement }>(`/cdas/${id}/cancel`, {
      reason,
      notes,
    }),

  /**
   * Edit an existing pending or rejected CDA (T043)
   * Requires version field for optimistic locking
   */
  edit: (id: string, editData: EditCDAInput) =>
    apiClient.patch<EditCDAResponse>(`/cdas/${id}`, editData),

  export: (format: 'csv' | 'excel' = 'csv') => apiClient.get<Blob>(`/cdas/export?format=${format}`),
};

/**
 * Edit CDA types (Phase 3 US1 - T043)
 */
export interface EditCDAInput {
  version: number; // Required for optimistic locking
  agentId?: string;
  agentName?: string;
  agentEmail?: string;
  agentPhone?: string;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  mlsNumber?: string;
  contractDate?: string;
  expires?: string;
  representation?: 'buyer' | 'seller' | 'tenant' | 'landlord';
  leaseCommissionBasis?: 'monthly' | 'annual';
  propertyClass?: 'residential' | 'commercial';
  sellerAgent?: string;
  buyerAgent?: string;
  escrowNumber?: string;
  escrowCompany?: string;
  escrowAddress?: string;
  escrowPhone?: string;
  escrowFax?: string;
  escrowAgent?: string;
  escrowAgentEmail?: string;
  // Legacy aliases accepted by backend for backward compatibility.
  titleCompanyName?: string;
  titleCompanyContact?: string;
  titleCompanyEmail?: string;
  titleCompanyPhone?: string;
  purchasePrice?: number;
  commissionRate?: number;
  commissionFlatFee?: number;
  totalCommissionDue?: number;
  brokerTransactionFee?: number;
  eoInsuranceFee?: number;
  annualDues?: number;
  duesPaymentDetails?: Array<{
    month: string;
    amount: number;
    type?: string;
  }>;
  duesDeductionOption?: 'current_balance' | 'anniversary' | 'none';
  duesDeductionAmount?: number;
  duesDeductionMonths?: Array<{
    month: string;
    amount: number;
  }>;
  otherDeductions?: number;
  otherDeductionsDescription?: string;
  agentCommissionPercentage?: number;
  bonusAmount?: number;
  bonusDescription?: string;
  referringBrokerName?: string;
  referringBrokerCompany?: string;
  referringBrokerEmail?: string;
  referringBrokerFee?: number;
  agentCreditAmount?: number;
  agentCreditAppliedTo?: string;
  depositReleasedToBrokerage?: number;
  agentNotes?: string;
  internalNotes?: string;
  totalOfficeFees?: number;
  netAgentCommission?: number;
}

export interface EditCDAResponse {
  status: 'success';
  message: string;
  data: {
    id: string;
    version: number;
    lastModified: string;
    status: string;
    netAgentCommission: number;
    totalCommissionDue: number;
  };
}

/**
 * Dashboard API calls
 */
export interface AgentStatistics {
  ytdTransactions: number;
  ytdGrossCommission: number;
  ytdNetCommission: number;
  pendingAmount: number;
}

export interface BrokerKPIs {
  totalClosed: number;
  grossCommission: string;
  netCommission: string;
  pendingCount: number;
  totalFees: string;
  avgCommissionPerDeal: string;
}

export interface BrokerDashboardStats {
  status: 'success';
  message: string;
  data: {
    period: string;
    dateRange: {
      start: string;
      end: string;
    };
    kpis: BrokerKPIs;
    trends: unknown[];
    agentPerformance: unknown[];
    topPerformers: unknown[];
  };
}

export const dashboardApi = {
  getAgentStats: () => apiClient.get<{ data: AgentStatistics }>('/dashboard/agent'),

  getBrokerStats: (params?: { period?: string; start?: string; end?: string }) => {
    const queryParams = new URLSearchParams(params as Record<string, string>).toString();
    return apiClient.get<BrokerDashboardStats>(
      `/dashboard/broker${queryParams ? `?${queryParams}` : ''}`
    );
  },
};

/**
 * Property API calls
 */
export interface PropertyData {
  id: string; // MLS Number
  address: string;
  city: string;
  state: string;
  zip: string;
  price?: number;
  listPrice?: number;
  beds?: number;
  baths?: number;
  sqft?: number;
  yearBuilt?: number;
  image?: string;
}

export const propertyApi = {
  getByMls: (mlsNumber: string) =>
    apiClient.get<{ data: PropertyData }>(`/properties/${mlsNumber}`),
};

/**
 * Approval API calls
 */
/**
 * Approval API calls
 */
export const approvalApi = {
  getPending: () => apiClient.get<{ data: CommissionDisbursement[] }>('/approvals/pending'),
};

/**
 * Title Company API calls
 */
export interface TitleCompany {
  id: string;
  name: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  activeFlag?: boolean;
}

export interface TitleCompanyListResponse {
  status: 'success';
  data: TitleCompany[];
  meta?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface TitleCompanyMutationResponse {
  status: 'success';
  message: string;
  data: TitleCompany;
}

export interface TitleCompanyListParams {
  query?: string;
  page?: number;
  pageSize?: number;
  includeInactive?: boolean;
}

export interface TitleCompanyImportNormalizedRow {
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface TitleCompanyImportCommitMeta {
  createdCount: number;
  reactivatedCount: number;
  skippedCount: number;
}

const handleRawApiError = async (response: Response): Promise<never> => {
  let payload: { message?: string } | null = null;
  try {
    payload = (await response.json()) as { message?: string };
  } catch {
    payload = null;
  }

  if (response.status === 401) {
    useAuthStore.getState().logout();
  }

  throw new ApiError(
    payload?.message || 'An error occurred',
    response.status,
    payload ?? undefined
  );
};

export const titleCompanyApi = {
  search: (query: string) =>
    apiClient.get<TitleCompanyListResponse>(`/title-companies?query=${encodeURIComponent(query)}`),

  list: (params: TitleCompanyListParams = {}) => {
    const query = new URLSearchParams();

    if (params.query) query.set('query', params.query);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.includeInactive !== undefined) {
      query.set('includeInactive', String(params.includeInactive));
    }

    const queryString = query.toString();
    return apiClient.get<TitleCompanyListResponse>(
      `/title-companies${queryString ? `?${queryString}` : ''}`
    );
  },

  create: (payload: {
    name: string;
    contactName?: string;
    email?: string;
    phone?: string;
    address?: string;
  }) => apiClient.post<TitleCompanyMutationResponse>('/title-companies', payload),

  update: (
    id: string,
    payload: {
      name?: string;
      contactName?: string;
      email?: string;
      phone?: string;
      address?: string;
    }
  ) => apiClient.patch<TitleCompanyMutationResponse>(`/title-companies/${id}`, payload),

  remove: (id: string) => apiClient.delete<TitleCompanyMutationResponse>(`/title-companies/${id}`),

  downloadImportTemplate: async (): Promise<Blob> => {
    const { accessToken } = useAuthStore.getState();
    const response = await fetch(`${API_BASE_URL}/title-companies/import/template`, {
      method: 'GET',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return handleRawApiError(response);
    }

    return response.blob();
  },

  previewImport: async (
    file: File
  ): Promise<ImportPreviewResponse<TitleCompanyImportNormalizedRow>> => {
    const { accessToken } = useAuthStore.getState();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/title-companies/import/preview`, {
      method: 'POST',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      return handleRawApiError(response);
    }

    return response.json() as Promise<ImportPreviewResponse<TitleCompanyImportNormalizedRow>>;
  },

  commitImport: async (file: File): Promise<ImportCommitResponse<TitleCompanyImportCommitMeta>> => {
    const { accessToken } = useAuthStore.getState();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/title-companies/import/commit`, {
      method: 'POST',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      return handleRawApiError(response);
    }

    return response.json() as Promise<ImportCommitResponse<TitleCompanyImportCommitMeta>>;
  },
};

/**
 * Audit Log API calls
 */
export interface AuditLogFilters {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  eventType?: string;
  entityType?: string;
  userId?: string;
  entityId?: string;
  action?: string;
}

export interface AuditLog {
  id: string;
  eventType: string;
  entityType?: string;
  entityId?: string;
  action: string;
  description?: string;
  userId?: string;
  userEmail?: string;
  userRole?: string;
  ipAddress?: string;
  userAgent?: string;
  status: string; // 'success' | 'failure'
  metadata?: Record<string, unknown>;
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  changedFields?: string[];
  createdAt: string;
  user?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface AuditLogListResponse {
  status: 'success';
  message: string;
  data: {
    data: AuditLog[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export const auditApi = {
  getLogs: (params: AuditLogFilters = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    if (params.eventType) query.set('eventType', params.eventType);
    if (params.entityType) query.set('entityType', params.entityType);
    if (params.userId) query.set('userId', params.userId);
    if (params.entityId) query.set('entityId', params.entityId);
    if (params.action) query.set('action', params.action);

    const queryString = query.toString();
    return apiClient.get<AuditLogListResponse>(
      `/audit-logs${queryString ? `?${queryString}` : ''}`
    );
  },
  getLogById: (id: string) => apiClient.get<{ data: AuditLog }>(`/audit-logs/${id}`),
};

/**
 * Agent API contracts
 */
export type AgentStatus = 'active' | 'inactive';
export type PlanStatus = 'active' | 'inactive';

// Plan types defined at bottom of file

export interface AgentRecord {
  id: string;
  userId?: string | null;
  firstName?: string;
  lastName?: string;
  fullName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  hireDate: string;
  terminationDate?: string | null;
  status: AgentStatus;
  compensationPlanId?: number | null;
  compensationPlan?: CompensationPlanSummary | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  photoUrl?: string | null;
  bio?: string | null;
  serviceArea?: string | null;
  nameSlug?: string | null;
  vCardUrl?: string | null;
  linktreeLink?: string | null;
  links?: Array<{ title: string; url: string }>;
  propertyImageUrls?: string[];
  linkedinUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  twitterUrl?: string | null;
  linktreeUrl?: string | null;
  isSso?: boolean;
  teamMemberId?: string | null;
}

// Plan types have been moved to the bottom of the file to consolidate definitions.

export interface CreateAgentRequest {
  firstName: string;
  lastName: string;
  fullName?: string; // Optional/legacy
  email: string;
  phone: string;
  licenseNumber: string;
  hireDate: string;
  terminationDate?: string | null;
  compensationPlanId?: number | null;
  status?: AgentStatus;
  password?: string;
}

export interface CreateAgentResponse {
  status: 'success';
  message: string;
  data: AgentRecord;
}

type AgentApiPayload = Omit<AgentRecord, 'status' | 'compensationPlan'> & {
  status: string;
  compensationPlan?: {
    id: number;
    planName: string;
    status: string;
  } | null;
  teamMemberId?: string | null;
};

interface AgentListApiResponse {
  status: 'success';
  message: string;
  data: {
    data: AgentApiPayload[];
    total: number;
  };
}

interface AgentSingleResponse {
  status: 'success';
  message: string;
  data: AgentApiPayload;
}

export interface AgentRecentProperty {
  cdaId: string;
  propertyAddress: string | null;
  grossCommission: number;
  netCommission: number;
  status: string;
  submissionDate: string;
}

export interface AgentDetailRecord {
  agent: AgentRecord;
  compensationPlan: {
    id: number;
    planName: string;
    brokerTransactionFee: number;
    eoInsuranceFee: number;
    annualDues: number;
    monthlyDues?: number;
    agentCommissionSplitPercentage: number;
    status: string;
  } | null;
  performance: {
    ytd: {
      totalTransactions: number;
      netCommission: string;
      avgCommissionPerDeal: string;
    };
  };
  recentProperties: AgentRecentProperty[];
}

interface AgentDetailApiResponse {
  status: 'success';
  message: string;
  data: AgentApiPayload & {
    agent: AgentApiPayload;
    compensationPlan: AgentDetailRecord['compensationPlan'];
    performance: AgentDetailRecord['performance'];
    recentProperties: AgentRecentProperty[];
  };
}

export interface ListAgentsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: AgentStatus;
  sortBy?: 'createdAt' | 'updatedAt' | 'fullName' | 'email' | 'hireDate';
  sortDirection?: 'asc' | 'desc';
}

export interface AgentListResult {
  agents: AgentRecord[];
  total: number;
}

export interface TransactionCoordinatorRecord {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | null;
  status: AgentStatus;
  role: 'transaction_coordinator';
  hireDate: string;
  createdAt: string;
  updatedAt: string;
  isSso: boolean;
}

type TransactionCoordinatorApiPayload = Omit<TransactionCoordinatorRecord, 'status'> & {
  status: string;
};

interface TransactionCoordinatorListApiResponse {
  status: 'success';
  message: string;
  data: {
    data: TransactionCoordinatorApiPayload[];
    total: number;
  };
}

export interface ListTransactionCoordinatorsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: AgentStatus;
  sortBy?: 'createdAt' | 'updatedAt' | 'fullName' | 'email' | 'hireDate';
  sortDirection?: 'asc' | 'desc';
}

export interface TransactionCoordinatorListResult {
  items: TransactionCoordinatorRecord[];
  total: number;
}

export type TeamMemberRole = 'agent' | 'broker' | 'admin' | 'transaction_coordinator';

export interface TeamMemberRecord {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | null;
  status: AgentStatus;
  role: TeamMemberRole;
  hireDate: string;
  createdAt: string;
  updatedAt: string;
  isSso: boolean;
  isLeadership: boolean;
  leadershipTitle: string | null;
  leadershipOrder: number | null;
  agentId: string | null;
  licenseNumber: string | null;
  nameSlug: string;
  photoUrl: string | null;
  bio: string | null;
  serviceArea: string | null;
  showOnWebsite: boolean;
}

type TeamMemberApiPayload = Omit<TeamMemberRecord, 'status'> & {
  status: string;
};

interface TeamMemberListApiResponse {
  status: 'success';
  message: string;
  data: {
    data: TeamMemberApiPayload[];
    total: number;
  };
}

interface TeamMemberSingleApiResponse {
  status: 'success';
  message: string;
  data: TeamMemberApiPayload;
}

export interface TeamMemberListResult {
  items: TeamMemberRecord[];
  total: number;
}

export type TeamMemberSortField =
  | 'fullName'
  | 'teamRoles'
  | 'role'
  | 'licenseNumber'
  | 'status'
  | 'websiteVisibility';

export interface ListTeamMembersQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: AgentStatus;
  sortBy?: TeamMemberSortField | 'createdAt' | 'updatedAt' | 'email' | 'hireDate';
  sortDirection?: 'asc' | 'desc';
}

export interface UpdateTeamMemberRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  hireDate: string;
  status: AgentStatus;
  role: TeamMemberRole;
  isLeadership: boolean;
  leadershipTitle: string | null;
  leadershipOrder: number | null;
  showOnWebsite: boolean;
  publicProfile: {
    nameSlug: string | null;
    bio: string | null;
    photoUrl: string | null;
  };
  agentProfile?: {
    licenseNumber: string | null;
    serviceArea: string | null;
  };
}

export interface AgentUpdatePayload {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  licenseNumber?: string;
  hireDate?: string;
  terminationDate?: string;
  status?: AgentStatus;
  compensationPlanId?: number | null;
  photoUrl?: string | null;
  bio?: string | null;
  serviceArea?: string | null;
  nameSlug?: string | null;
  vCardUrl?: string | null;
  linktreeLink?: string | null;
  links?: Array<{ title: string; url: string }> | null;
  propertyImageUrls?: string[] | null;
  linkedinUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  twitterUrl?: string | null;
  linktreeUrl?: string | null;
}

const normalizePlan = (
  plan: AgentApiPayload['compensationPlan']
): CompensationPlanSummary | null => {
  if (!plan) {
    return null;
  }

  const normalizedStatus: PlanStatus =
    plan.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active';

  return {
    id: plan.id,
    planName: plan.planName,
    status: normalizedStatus,
  };
};

const normalizeAgent = (agent: AgentApiPayload): AgentRecord => ({
  ...agent,
  status: agent.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active',
  compensationPlanId:
    typeof agent.compensationPlanId === 'number'
      ? agent.compensationPlanId
      : (agent.compensationPlan?.id ?? null),
  compensationPlan: normalizePlan(agent.compensationPlan),
  terminationDate: agent.terminationDate ?? null,
  photoUrl: agent.photoUrl ?? null,
  bio: agent.bio ?? null,
  serviceArea: agent.serviceArea ?? null,
  nameSlug: agent.nameSlug ?? null,
  vCardUrl: agent.vCardUrl ?? null,
  linktreeLink: agent.linktreeLink ?? null,
  links: Array.isArray(agent.links) ? (agent.links as Array<{ title: string; url: string }>) : [],
  propertyImageUrls: Array.isArray(agent.propertyImageUrls)
    ? (agent.propertyImageUrls as string[])
    : [],
  linkedinUrl: agent.linkedinUrl ?? null,
  facebookUrl: agent.facebookUrl ?? null,
  instagramUrl: agent.instagramUrl ?? null,
  twitterUrl: agent.twitterUrl ?? null,
  linktreeUrl: agent.linktreeUrl ?? null,
  teamMemberId: agent.teamMemberId ?? null,
});

const normalizeTransactionCoordinator = (
  coordinator: TransactionCoordinatorApiPayload
): TransactionCoordinatorRecord => ({
  ...coordinator,
  status: coordinator.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active',
});

const normalizeTeamMember = (member: TeamMemberApiPayload): TeamMemberRecord => ({
  ...member,
  status: member.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active',
});

export const agentApi = {
  create: (payload: CreateAgentRequest) => apiClient.post<CreateAgentResponse>('/agents', payload),
  list: async (params: ListAgentsQuery = {}): Promise<AgentListResult> => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);

    const queryString = query.toString();
    const response = await apiClient.get<AgentListApiResponse>(
      `/agents${queryString ? `?${queryString}` : ''}`
    );

    return {
      agents: response.data.data.map((agent) => normalizeAgent(agent)),
      total: response.data.total,
    };
  },
  getById: async (agentId: string): Promise<AgentRecord> => {
    const response = await apiClient.get<AgentSingleResponse>(`/agents/${agentId}`);
    return normalizeAgent(response.data);
  },
  getDetailById: async (agentId: string): Promise<AgentDetailRecord> => {
    const response = await apiClient.get<AgentDetailApiResponse>(`/agents/${agentId}`);
    return {
      agent: normalizeAgent(response.data.agent),
      compensationPlan: response.data.compensationPlan,
      performance: response.data.performance,
      recentProperties: response.data.recentProperties,
    };
  },
  update: async (agentId: string, payload: AgentUpdatePayload): Promise<AgentRecord> => {
    const response = await apiClient.patch<AgentSingleResponse>(`/agents/${agentId}`, payload);
    return normalizeAgent(response.data);
  },
  uploadPropertyImages: async (agentId: string, files: File[]): Promise<string[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    const { accessToken } = useAuthStore.getState();
    const response = await fetch(`${API_BASE_URL}/agents/${agentId}/property-images`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: accessToken ? { Authorization: ['Bearer', accessToken].join(' ') } : undefined,
    });

    if (!response.ok) {
      const error = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new ApiError(error?.message ?? 'Failed to upload property images', response.status);
    }

    const result = (await response.json()) as { data: { urls: string[] } };
    return result.data.urls;
  },
  deactivate: async (agentId: string): Promise<AgentRecord> => {
    const response = await apiClient.post<AgentSingleResponse>(`/agents/${agentId}/deactivate`);
    return normalizeAgent(response.data);
  },
  reactivate: async (agentId: string): Promise<AgentRecord> => {
    const response = await apiClient.post<AgentSingleResponse>(`/agents/${agentId}/reactivate`);
    return normalizeAgent(response.data);
  },
  getCapProgress: async (agentId: string): Promise<CapProgress> => {
    const response = await apiClient.get<{ status: string; data: CapProgress }>(
      `/agents/${agentId}/cap-progress`
    );
    return response.data;
  },
  getTransactions: async (
    agentId: string,
    options?: TransactionQueryOptions
  ): Promise<TransactionsResult> => {
    const query = new URLSearchParams();
    if (options?.page) query.set('page', String(options.page));
    if (options?.pageSize) query.set('pageSize', String(options.pageSize));
    if (options?.startDate) query.set('startDate', options.startDate);
    if (options?.endDate) query.set('endDate', options.endDate);
    if (options?.source) query.set('source', options.source);
    const queryString = query.toString();
    const response = await apiClient.get<{ status: string; data: TransactionsResult }>(
      `/agents/${agentId}/transactions${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },
};

type AgentDetailsQueryKey = readonly ['agent-details', string];
type AgentDetailsQueryOptions = UndefinedInitialDataOptions<
  AgentDetailRecord,
  ApiError,
  AgentDetailRecord,
  AgentDetailsQueryKey
> & {
  queryKey: DataTag<AgentDetailsQueryKey, AgentDetailRecord, ApiError>;
};

export const getAgentDetailsQueryOptions = (agentId: string): AgentDetailsQueryOptions =>
  queryOptions<AgentDetailRecord, ApiError, AgentDetailRecord, AgentDetailsQueryKey>({
    queryKey: ['agent-details', agentId],
    queryFn: async () => agentApi.getDetailById(agentId),
    staleTime: 60000,
  });

export interface CreateTransactionCoordinatorRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  hireDate: string;
  password?: string;
  status?: AgentStatus;
}

export interface UpdateTransactionCoordinatorRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  hireDate?: string;
  status?: AgentStatus;
}

export interface CreateTransactionCoordinatorResponse {
  status: 'success';
  message: string;
  data: TransactionCoordinatorRecord;
}

export const teamApi = {
  listMembers: async (params: ListTeamMembersQuery = {}): Promise<TeamMemberListResult> => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);

    const queryString = query.toString();
    const response = await apiClient.get<TeamMemberListApiResponse>(
      `/team/members${queryString ? `?${queryString}` : ''}`
    );

    return {
      items: response.data.data.map(normalizeTeamMember),
      total: response.data.total,
    };
  },

  updateMember: async (
    userId: string,
    payload: UpdateTeamMemberRequest
  ): Promise<TeamMemberRecord> => {
    const response = await apiClient.patch<TeamMemberSingleApiResponse>(
      `/team/members/${userId}`,
      payload
    );
    return normalizeTeamMember(response.data);
  },

  deactivateMember: async (userId: string): Promise<TeamMemberRecord> => {
    const response = await apiClient.post<TeamMemberSingleApiResponse>(
      `/team/members/${userId}/deactivate`
    );
    return normalizeTeamMember(response.data);
  },
  deleteMember: async (userId: string): Promise<TeamMemberRecord> => {
    const response = await apiClient.delete<TeamMemberSingleApiResponse>(`/team/members/${userId}`);
    return normalizeTeamMember(response.data);
  },

  listTransactionCoordinators: async (
    params: ListTransactionCoordinatorsQuery = {}
  ): Promise<TransactionCoordinatorListResult> => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);

    const queryString = query.toString();
    const response = await apiClient.get<TransactionCoordinatorListApiResponse>(
      `/team/transaction-coordinators${queryString ? `?${queryString}` : ''}`
    );

    return {
      items: response.data.data.map((coordinator) => normalizeTransactionCoordinator(coordinator)),
      total: response.data.total,
    };
  },

  createTransactionCoordinator: (payload: CreateTransactionCoordinatorRequest) =>
    apiClient.post<CreateTransactionCoordinatorResponse>('/team/transaction-coordinators', payload),

  updateTransactionCoordinator: (tcId: string, payload: UpdateTransactionCoordinatorRequest) =>
    apiClient.put<CreateTransactionCoordinatorResponse>(
      `/team/transaction-coordinators/${tcId}`,
      payload
    ),
};

export interface DuesStatus {
  balance: number;
  pastDueAmount: number;
  currentDueAmount: number;
  isCurrentMonthPaid: boolean;
  nextDueMonth: string;
  paidThroughDate: string | null;
}

export interface PaymentOption {
  month: string;
  amount: number;
  label: string;
  isPastDue: boolean;
  isCurrent: boolean;
  isFuture: boolean;
  isRequired: boolean;
}

export interface DuesHistoryTransaction {
  id: string;
  transactionType: string;
  amount: string; // Decimal comes as string from API usually
  balanceAfter: string;
  transactionDate: string;
  effectiveMonth: string;
  description: string | null;
  referenceId: string | null;
  referenceType: string | null;
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface DuesPaymentOptionsResponse {
  status: DuesStatus;
  options: PaymentOption[];
  monthlyRate: number;
}

export interface DuesDeductionOption {
  option: 'current_balance' | 'anniversary' | 'none';
  label: string;
  description: string;
  amount: number;
  months: Array<{
    month: string;
    amount: number;
    type: 'past_due' | 'current' | 'future';
  }>;
}

export interface DuesDeductionOptionsResponse {
  status: DuesStatus;
  options: DuesDeductionOption[];
  monthlyRate: number;
}

export const agentDuesApi = {
  getOptions: (agentId: string) =>
    apiClient.get<{ data: DuesPaymentOptionsResponse }>(`/agents/${agentId}/dues/options`),

  // ===== NEW: Get dues deduction options for CDA submission (AP-3) =====
  getDeductionOptions: (agentId: string) =>
    apiClient.get<{ data: DuesDeductionOptionsResponse }>(
      `/agents/${agentId}/dues/deduction-options`
    ),

  getHistory: (agentId: string) =>
    apiClient.get<{ data: DuesHistoryTransaction[] }>(`/agents/${agentId}/dues/history`),

  recordOffline: (
    agentId: string,
    payload: {
      amount: number;
      months: string[];
      referenceId?: string;
      referenceType: 'CHECK' | 'CREDIT_CARD' | 'ZELLE' | 'OTHER';
      description?: string;
    }
  ) => apiClient.post(`/agents/${agentId}/dues/offline-payment`, payload),
};

// ===== Cap Progress & Unified Transaction Ledger types (AP-37) =====

export interface CapProgress {
  capAmount: number;
  earnedThisYear: number;
  remaining: number;
  percentUsed: number;
  anniversaryYearStart: string;
  anniversaryYearEnd: string;
  capEnforced: boolean;
}

export interface UnifiedTransaction {
  id: string;
  date: string;
  type:
    | 'ASSESSMENT'
    | 'PAYMENT_CDA'
    | 'PAYMENT_OFFLINE'
    | 'ADJUSTMENT'
    | 'COMMISSION_AGENT'
    | 'FEE_TRANSACTION'
    | 'FEE_EO';
  description: string;
  amount: number;
  balanceAfter: number | null;
  referenceId: string | null;
  referenceType: string | null;
  source: 'dues' | 'cda';
}

export interface TransactionQueryOptions {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  source?: 'dues' | 'cda' | 'all';
}

export interface TransactionsResult {
  items: UnifiedTransaction[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UseAgentsQueryOptions {
  enabled?: boolean;
  staleTime?: number;
}

export const useAgentsQuery = (
  params: ListAgentsQuery,
  options?: UseAgentsQueryOptions
): UseQueryResult<AgentListResult, ApiError> =>
  useQuery<AgentListResult, ApiError>({
    queryKey: ['agents', params],
    queryFn: () => agentApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: options?.staleTime ?? 60000,
    enabled: options?.enabled ?? true,
  });

export const useTransactionCoordinatorsQuery = (
  params: ListTransactionCoordinatorsQuery,
  options?: UseAgentsQueryOptions
): UseQueryResult<TransactionCoordinatorListResult, ApiError> =>
  useQuery<TransactionCoordinatorListResult, ApiError>({
    queryKey: ['team', 'transaction-coordinators', params],
    queryFn: () => teamApi.listTransactionCoordinators(params),
    placeholderData: keepPreviousData,
    staleTime: options?.staleTime ?? 60000,
    enabled: options?.enabled ?? true,
  });

export const useTeamMembersQuery = (
  params: ListTeamMembersQuery,
  options?: UseAgentsQueryOptions
): UseQueryResult<TeamMemberListResult, ApiError> =>
  useQuery<TeamMemberListResult, ApiError>({
    queryKey: ['team', 'members', params],
    queryFn: () => teamApi.listMembers(params),
    placeholderData: keepPreviousData,
    staleTime: options?.staleTime ?? 60000,
    enabled: options?.enabled ?? true,
  });

export interface UpdateTeamMemberVariables {
  userId: string;
  payload: UpdateTeamMemberRequest;
}

export const useUpdateTeamMember = (): UseMutationResult<
  TeamMemberRecord,
  ApiError,
  UpdateTeamMemberVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<TeamMemberRecord, ApiError, UpdateTeamMemberVariables>({
    mutationKey: ['team', 'members', 'update'],
    mutationFn: ({ userId, payload }) => teamApi.updateMember(userId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['team', 'members'] }),
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
      ]);
    },
  });
};

export const useDeactivateTeamMember = (): UseMutationResult<
  TeamMemberRecord,
  ApiError,
  { userId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<TeamMemberRecord, ApiError, { userId: string }>({
    mutationKey: ['team', 'members', 'deactivate'],
    mutationFn: ({ userId }) => teamApi.deactivateMember(userId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['team', 'members'] }),
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
      ]);
    },
  });
};
export const useDeleteTeamMember = (): UseMutationResult<
  TeamMemberRecord,
  ApiError,
  { userId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<TeamMemberRecord, ApiError, { userId: string }>({
    mutationKey: ['team', 'members', 'delete'],
    mutationFn: ({ userId }) => teamApi.deleteMember(userId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['team', 'members'] }),
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
      ]);
    },
  });
};

export interface UseCreateTransactionCoordinatorOptions {
  onSuccess?: (coordinator: TransactionCoordinatorRecord) => Promise<void> | void;
  onError?: (error: ApiError) => void;
}

export const useCreateTransactionCoordinator = (
  options?: UseCreateTransactionCoordinatorOptions
): UseMutationResult<
  TransactionCoordinatorRecord,
  ApiError,
  CreateTransactionCoordinatorRequest
> => {
  const queryClient = useQueryClient();

  return useMutation<TransactionCoordinatorRecord, ApiError, CreateTransactionCoordinatorRequest>({
    mutationKey: ['team', 'transaction-coordinators', 'create'],
    mutationFn: (payload) =>
      teamApi.createTransactionCoordinator(payload).then((response) => response.data),
    onSuccess: async (coordinator) => {
      await queryClient.invalidateQueries({ queryKey: ['team', 'transaction-coordinators'] });

      if (options?.onSuccess) {
        await options.onSuccess(coordinator);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

export interface UseUpdateTransactionCoordinatorOptions {
  onSuccess?: (coordinator: TransactionCoordinatorRecord) => Promise<void> | void;
  onError?: (error: ApiError) => void;
}

export interface UpdateTransactionCoordinatorVariables {
  tcId: string;
  payload: UpdateTransactionCoordinatorRequest;
}

export const useUpdateTransactionCoordinator = (
  options?: UseUpdateTransactionCoordinatorOptions
): UseMutationResult<
  TransactionCoordinatorRecord,
  ApiError,
  UpdateTransactionCoordinatorVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<TransactionCoordinatorRecord, ApiError, UpdateTransactionCoordinatorVariables>(
    {
      mutationKey: ['team', 'transaction-coordinators', 'update'],
      mutationFn: ({ tcId, payload }) =>
        teamApi.updateTransactionCoordinator(tcId, payload).then((response) => response.data),
      onSuccess: async (coordinator) => {
        await queryClient.invalidateQueries({ queryKey: ['team', 'transaction-coordinators'] });

        if (options?.onSuccess) {
          await options.onSuccess(coordinator);
        }
      },
      onError: (error) => {
        if (options?.onError) {
          options.onError(error);
        }
      },
    }
  );
};

export interface UpdateAgentVariables {
  agentId: string;
  payload: AgentUpdatePayload;
}

export interface UseUpdateAgentOptions {
  onSuccess?: (agent: AgentRecord) => Promise<void> | void;
  onError?: (error: ApiError) => void;
}

export const useUpdateAgent = (
  options?: UseUpdateAgentOptions
): UseMutationResult<AgentRecord, ApiError, UpdateAgentVariables> => {
  const queryClient = useQueryClient();

  return useMutation<AgentRecord, ApiError, UpdateAgentVariables>({
    mutationKey: ['agents', 'update'],
    mutationFn: ({ agentId, payload }) => agentApi.update(agentId, payload),
    onSuccess: async (agent, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
        queryClient.invalidateQueries({ queryKey: ['agent', variables.agentId] }),
      ]);

      if (options?.onSuccess) {
        await options.onSuccess(agent);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

export interface UseAgentStatusOptions {
  onSuccess?: (agent: AgentRecord) => Promise<void> | void;
  onError?: (error: ApiError) => void;
}

const useAgentStatusMutationInternal = (
  action: 'deactivate' | 'reactivate',
  options?: UseAgentStatusOptions
): UseMutationResult<AgentRecord, ApiError, string> => {
  const queryClient = useQueryClient();

  return useMutation<AgentRecord, ApiError, string>({
    mutationKey: ['agents', action],
    mutationFn: (agentId: string) => {
      if (action === 'deactivate') {
        return agentApi.deactivate(agentId);
      }
      return agentApi.reactivate(agentId);
    },
    onSuccess: async (agent, agentId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
        queryClient.invalidateQueries({ queryKey: ['agent', agentId] }),
      ]);

      if (options?.onSuccess) {
        await options.onSuccess(agent);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

export const useDeactivateAgent = (
  options?: UseAgentStatusOptions
): UseMutationResult<AgentRecord, ApiError, string> =>
  useAgentStatusMutationInternal('deactivate', options);

export const useReactivateAgent = (
  options?: UseAgentStatusOptions
): UseMutationResult<AgentRecord, ApiError, string> =>
  useAgentStatusMutationInternal('reactivate', options);

export interface UseCreateAgentOptions {
  onSuccess?: (response: CreateAgentResponse) => Promise<void> | void;
  onError?: (error: ApiError) => void;
}

// prettier-ignore
export type CreateAgentMutation = UseMutationResult<CreateAgentResponse, ApiError, CreateAgentRequest, unknown>;

export const useCreateAgent = (options?: UseCreateAgentOptions): CreateAgentMutation => {
  const queryClient = useQueryClient();

  return useMutation<CreateAgentResponse, ApiError, CreateAgentRequest>({
    mutationKey: ['agents', 'create'],
    mutationFn: (payload) => agentApi.create(payload),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['agents'] });
      if (options?.onSuccess) {
        await options.onSuccess(response);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

type Decimalish = string | number | null | undefined;

const parseDecimal = (value: Decimalish): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};

// Plan specific types - Consolidating duplicates

// 1. Base Payload (what comes from the API mostly)
export interface PlanApiPayload {
  id: number;
  planName: string;
  brokerTransactionFee: Decimalish;
  brokerTransactionFeeRequired?: boolean;
  postCapTransactionFee?: Decimalish;
  postCapTransactionFeeRequired?: boolean;
  eoInsuranceFee: Decimalish;
  eoInsuranceFeeRequired?: boolean;
  monthlyDues?: Decimalish;
  monthlyDuesRequired?: boolean;
  annualDues?: Decimalish;
  annualDuesRequired?: boolean;
  transactionCoordinatorFee: Decimalish;
  transactionCoordinatorFeeRequired?: boolean;
  agentCommissionSplitPercentage: Decimalish;
  cappedAnnualCommission: Decimalish;
  cappedAnnualCommissionRequired?: boolean;
  status: string;
  numberOfAgentsAssigned: number;
  createdAt: string;
  updatedAt: string;
}

const normalizePlanRecord = (plan: PlanApiPayload): CompensationPlanRecord => ({
  id: plan.id,
  planName: plan.planName,
  brokerTransactionFee: parseDecimal(plan.brokerTransactionFee),
  brokerTransactionFeeRequired: plan.brokerTransactionFeeRequired ?? false,
  postCapTransactionFee: parseDecimal(plan.postCapTransactionFee),
  postCapTransactionFeeRequired: plan.postCapTransactionFeeRequired ?? false,
  eoInsuranceFee: parseDecimal(plan.eoInsuranceFee),
  eoInsuranceFeeRequired: plan.eoInsuranceFeeRequired ?? false,
  monthlyDues: parseDecimal(plan.monthlyDues ?? plan.annualDues),
  monthlyDuesRequired: plan.monthlyDuesRequired ?? plan.annualDuesRequired ?? false,
  transactionCoordinatorFee: parseDecimal(plan.transactionCoordinatorFee),
  transactionCoordinatorFeeRequired: plan.transactionCoordinatorFeeRequired ?? false,
  agentCommissionSplitPercentage: parseDecimal(plan.agentCommissionSplitPercentage),
  cappedAnnualCommission: parseDecimal(plan.cappedAnnualCommission),
  cappedAnnualCommissionRequired: plan.cappedAnnualCommissionRequired ?? true,
  status: plan.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active',
  numberOfAgentsAssigned: plan.numberOfAgentsAssigned ?? 0,
  createdAt: plan.createdAt,
  updatedAt: plan.updatedAt,
});

// 2. Local Record (normalized)
export interface CompensationPlanSummary {
  id: number;
  planName: string;
  status: PlanStatus;
}

export interface CompensationPlanRecord {
  id: number;
  planName: string;
  brokerTransactionFee: number;
  brokerTransactionFeeRequired: boolean;
  postCapTransactionFee: number;
  postCapTransactionFeeRequired: boolean;
  eoInsuranceFee: number;
  eoInsuranceFeeRequired: boolean;
  monthlyDues: number;
  monthlyDuesRequired: boolean;
  transactionCoordinatorFee: number;
  transactionCoordinatorFeeRequired: boolean;
  agentCommissionSplitPercentage: number;
  cappedAnnualCommission: number;
  cappedAnnualCommissionRequired: boolean;
  status: PlanStatus;
  numberOfAgentsAssigned: number;
  createdAt: string;
  updatedAt: string;
}

// 3. API Response Wrappers

export interface CreatePlanRequest {
  planName: string;
  brokerTransactionFee: number;
  brokerTransactionFeeRequired: boolean;
  postCapTransactionFee: number;
  postCapTransactionFeeRequired: boolean;
  eoInsuranceFee: number;
  eoInsuranceFeeRequired: boolean;
  monthlyDues: number;
  monthlyDuesRequired: boolean;
  transactionCoordinatorFee: number;
  transactionCoordinatorFeeRequired: boolean;
  agentCommissionSplitPercentage: number;
  cappedAnnualCommission: number;
  cappedAnnualCommissionRequired: boolean;
  status: PlanStatus;
}

export interface UpdatePlanRequest {
  planName?: string;
  brokerTransactionFee?: number;
  brokerTransactionFeeRequired?: boolean;
  postCapTransactionFee?: number;
  postCapTransactionFeeRequired?: boolean;
  eoInsuranceFee?: number;
  eoInsuranceFeeRequired?: boolean;
  monthlyDues?: number;
  monthlyDuesRequired?: boolean;
  transactionCoordinatorFee?: number;
  transactionCoordinatorFeeRequired?: boolean;
  agentCommissionSplitPercentage?: number;
  cappedAnnualCommission?: number;
  cappedAnnualCommissionRequired?: boolean;
  status?: PlanStatus;
}

export interface PlanListQuery {
  status?: PlanStatus | 'all';
}

export const planApi = {
  async list(params: PlanListQuery = {}): Promise<CompensationPlanRecord[]> {
    const query = new URLSearchParams();
    if (params.status && params.status !== 'all') {
      query.set('status', params.status);
    }

    const response = await apiClient.get<PlanListApiResponse>(
      `/plans${query.toString() ? `?${query.toString()}` : ''}`
    );

    return response.data.map((plan) => normalizePlanRecord(plan));
  },
  async create(payload: CreatePlanRequest): Promise<CompensationPlanRecord> {
    const response = await apiClient.post<CreatePlanResponse>('/plans', payload);
    return normalizePlanRecord(response.data);
  },
  async getById(planId: number): Promise<CompensationPlanRecord> {
    const response = await apiClient.get<PlanSingleApiResponse>(`/plans/${planId}`);
    return normalizePlanRecord(response.data);
  },
  async update(planId: number, payload: UpdatePlanRequest): Promise<CompensationPlanRecord> {
    const response = await apiClient.patch<PlanSingleApiResponse>(`/plans/${planId}`, payload);
    return normalizePlanRecord(response.data);
  },
  async deactivate(planId: number): Promise<CompensationPlanRecord> {
    const response = await apiClient.post<PlanSingleApiResponse>(`/plans/${planId}/deactivate`);
    return normalizePlanRecord(response.data);
  },
  async reactivate(planId: number): Promise<CompensationPlanRecord> {
    const response = await apiClient.post<PlanSingleApiResponse>(`/plans/${planId}/reactivate`);
    return normalizePlanRecord(response.data);
  },
};

type PlanQueryKey = readonly ['plan', number];
type PlanQueryOptions = UndefinedInitialDataOptions<
  CompensationPlanRecord,
  ApiError,
  CompensationPlanRecord,
  PlanQueryKey
> & {
  queryKey: DataTag<PlanQueryKey, CompensationPlanRecord, ApiError>;
};

export const getPlanQueryOptions = (id: number): PlanQueryOptions =>
  queryOptions<CompensationPlanRecord, ApiError, CompensationPlanRecord, PlanQueryKey>({
    queryKey: ['plan', id],
    queryFn: async () => planApi.getById(id),
  });

export const usePlanQuery = (
  id: number,
  options?: { enabled?: boolean }
): UseQueryResult<CompensationPlanRecord, ApiError> =>
  useQuery({
    ...getPlanQueryOptions(id),
    enabled: options?.enabled,
  });

export interface UsePlansQueryOptions {
  enabled?: boolean;
  staleTime?: number;
}

export const usePlansQuery = (
  params?: PlanListQuery,
  options?: UsePlansQueryOptions
): UseQueryResult<CompensationPlanRecord[], ApiError> =>
  useQuery<CompensationPlanRecord[], ApiError>({
    queryKey: ['plans', params ?? {}],
    queryFn: () => planApi.list(params ?? {}),
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });

export interface UseCreatePlanOptions {
  onSuccess?: (plan: CompensationPlanRecord) => Promise<void> | void;
  onError?: (error: ApiError) => void;
}

export const useCreatePlan = (
  options?: UseCreatePlanOptions
): UseMutationResult<CompensationPlanRecord, ApiError, CreatePlanRequest> => {
  const queryClient = useQueryClient();

  return useMutation<CompensationPlanRecord, ApiError, CreatePlanRequest>({
    mutationKey: ['plans', 'create'],
    mutationFn: (payload) => planApi.create(payload),
    onSuccess: async (plan) => {
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
      if (options?.onSuccess) {
        await options.onSuccess(plan);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

export interface PlanUpdateVariables {
  planId: number;
  payload: UpdatePlanRequest;
}

export interface UsePlanMutationOptions {
  onSuccess?: (plan: CompensationPlanRecord) => Promise<void> | void;
  onError?: (error: ApiError) => void;
}

export const useUpdatePlan = (
  options?: UsePlanMutationOptions
): UseMutationResult<CompensationPlanRecord, ApiError, PlanUpdateVariables> => {
  const queryClient = useQueryClient();

  return useMutation<CompensationPlanRecord, ApiError, PlanUpdateVariables>({
    mutationKey: ['plans', 'update'],
    mutationFn: ({ planId, payload }) => planApi.update(planId, payload),
    onSuccess: async (plan) => {
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
      if (options?.onSuccess) {
        await options.onSuccess(plan);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

export const usePlanStatusTransition = (
  action: 'deactivate' | 'reactivate',
  options?: UsePlanMutationOptions
): UseMutationResult<CompensationPlanRecord, ApiError, number> => {
  const queryClient = useQueryClient();

  return useMutation<CompensationPlanRecord, ApiError, number>({
    mutationKey: ['plans', action],
    mutationFn: (planId: number) => planApi[action](planId),
    onSuccess: async (plan) => {
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
      if (options?.onSuccess) {
        await options.onSuccess(plan);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};
